import { Box, Typography } from '@mui/material'

interface FigureProps {
  /** Read out to screen readers in place of the drawing. */
  title: string
  /** Printed under the figure. Should say what to notice, not restate it. */
  caption?: string
  children: React.ReactNode
}

/**
 * Wrapper for the inline-SVG diagrams.
 *
 * Reason for the aria plumbing: the diagrams carry real content, so they are
 * `role="img"` with a title, and the SVG itself is hidden from the tree —
 * otherwise every stray <text> node is announced in drawing order, which is
 * not reading order.
 */
export function Figure({ title, caption, children }: FigureProps) {
  return (
    <Box
      component="figure"
      role="img"
      aria-label={title}
      sx={{ m: 0, my: 4 }}
    >
      <Box aria-hidden sx={{ '& svg': { display: 'block', width: '100%', height: 'auto' } }}>
        {children}
      </Box>
      {caption && (
        <Typography
          component="figcaption"
          variant="caption"
          sx={{ display: 'block', mt: 1.5, color: 'var(--text-faint)', lineHeight: 1.6 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  )
}

/** Shared SVG text presets. Kept here so all four diagrams set type identically. */
export const svgType = {
  label: {
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    fontWeight: 500,
    fill: 'var(--text)',
  },
  faint: {
    fontFamily: 'var(--font-sans)',
    fontSize: 11,
    fontWeight: 400,
    fill: 'var(--text-faint)',
  },
  num: {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    fontWeight: 500,
  },
  numSmall: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    fontWeight: 500,
  },
} as const
